/**
 * Matriz RBAC do Sentinela USE — single source of truth.
 *
 * Backend valida sempre via podeExecutar(). Frontend usa para UX.
 * Scoping de dados (quem vê o quê) é feito por filtros de query,
 * NÃO por permissão.
 */

export const ROLES = [
  "master",
  "seguranca_admin",
  "noc",
  "compliance",
  "auditor",
  /**
   * Financeiro: entra APENAS na tela de circuitos, apenas para bloquear e
   * desbloquear cliente. Não é NOC com menos coisas — é um papel próprio,
   * porque encaixar o financeiro em `noc` daria a ele alertas, forense,
   * ofícios e o resto da operação de segurança, que não é assunto dele.
   * Menor privilégio de verdade é lista curta, não lista grande com exceções.
   */
  "financeiro",
] as const;

export type Role = (typeof ROLES)[number];

export const ROLE_LABEL: Record<Role, string> = {
  master: "Master",
  seguranca_admin: "Admin de Segurança",
  noc: "NOC",
  compliance: "Compliance",
  auditor: "Auditor",
  financeiro: "Financeiro",
};

export const PERMISSOES = {
  // Alertas / Incidentes
  "alertas.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "alertas.editar": ["master", "seguranca_admin", "noc"],
  "alertas.aprovar_carta": ["master", "seguranca_admin", "noc"],
  "alertas.escalar": ["master", "seguranca_admin", "noc"],
  "alertas.arquivar": ["master", "seguranca_admin"],
  "alertas.reprocessar": ["master", "seguranca_admin"],

  // Cartas
  "cartas.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "cartas.editar_template": ["master", "seguranca_admin"],
  "cartas.reenviar": ["master", "seguranca_admin", "noc"],
  "cartas.cancelar": ["master", "seguranca_admin", "noc"],

  // Clientes
  "clientes.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "clientes.vincular_manual": ["master", "seguranca_admin", "noc"],
  // Política: mudar regras que afetam o pipeline (B2B, quarentena, tags,
  // política de notificação, etc).
  // Inclui o NOC por decisão do gestor (14/08/2026): é o time que trata o
  // alerta e conhece o cliente, e travar a política num papel que não está de
  // plantão emperrava a operação. ATENÇÃO ao alcance: com ela o NOC pode pôr
  // um cliente em "automático", e o sistema passa a notificar sem aprovação
  // humana. As barreiras que continuam valendo são os guard-rails do pipeline
  // e a auditoria (toda alteração fica registrada com autor).
  "clientes.editar_politica": ["master", "seguranca_admin", "noc"],
  // Editar contatos/dados de comunicação (emails, telefones, contatos,
  // anotações, importar IXC). NOC precisa pra tratar alertas — adicionar
  // contato encontrado durante triagem, importar atualização do IXC, etc.
  // Não inclui mudanças de POLÍTICA, só de DADOS.
  "clientes.editar_contatos": ["master", "seguranca_admin", "noc"],
  "clientes.quarentenar": ["master", "seguranca_admin", "noc"],
  // Direito de eliminação LGPD Art. 18 VI — apenas master pode acionar
  // (operação destrutiva, anonimiza cartas + apaga PII associada)
  "clientes.esquecer_lgpd": ["master"],

  // Configurações / Integrações
  "configuracoes.ver": ["master", "seguranca_admin", "compliance", "auditor"],
  "configuracoes.editar": ["master", "seguranca_admin"],
  "integracoes.gerenciar": ["master", "seguranca_admin"],

  // Auditoria
  "auditoria.ver": ["master", "seguranca_admin", "compliance", "auditor"],
  "auditoria.exportar": ["master", "seguranca_admin", "compliance"],
  // Dashboard de ranking de atividade dos usuários — master + seguranca_admin.
  // Expõe métricas agregadas (acessos, ações por tipo, atividade temporal)
  // que servem como termômetro gerencial. Restrito a quem tem visão de gestão
  // pra evitar uso como ferramenta de vigilância entre pares.
  "auditoria.dashboard": ["master", "seguranca_admin"],

  // ChangeLog — histórico oficial de alterações da plataforma.
  // VER é aberto a todos os papéis (transparência operacional pra equipe
  // saber o que mudou e quando). EDITAR fica restrito a master pra
  // preservar integridade do histórico — quem registra é Segurança da
  // Informação. Auditor também vê pra fiscalizar publicações.
  "changelog.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "changelog.editar": ["master"],

  // Painel de troca de prefixo IP — operação de NOC + governança.
  // NOC cria/envia/executa (operação diária). Compliance vê pra auditoria.
  // Auditor não está aqui — não é escopo do papel.
  "trocas.ver": ["master", "seguranca_admin", "noc", "compliance"],
  "trocas.criar": ["master", "seguranca_admin", "noc"],
  "trocas.enviar": ["master", "seguranca_admin", "noc"],
  "trocas.executar": ["master", "seguranca_admin", "noc"],

  // Forense CGNAT — integração com a API forense USE (5.9.81.213).
  // Ofícios judiciais tratam PII sensível LGPD → acesso restrito a
  // master/seguranca_admin/compliance (Auditor lê pra fiscalizar).
  // Console técnico operacional abre pra NOC também — útil pra investigar
  // alertas/incidentes sem precisar passar por outra área.
  "forense.ver": ["master", "seguranca_admin", "compliance", "auditor"],
  "forense.consultar_tecnico": ["master", "seguranca_admin", "noc"],
  "forense.oficio_gerenciar": ["master", "seguranca_admin", "compliance"],
  "forense.exportar": ["master", "seguranca_admin", "compliance"],

  // Infraestrutura — módulo de logs de equipamentos não-CGNAT
  // (Cisco ASR, switches, OLTs, BNGs etc.). Cadastro/edição/desativação
  // é operação de governança restrita a master — único papel que toca o
  // inventário. Visualização aberta pra todos os admins porque ajuda
  // troubleshooting de NOC (procurar LINK_DOWN, BGP_DOWN, LOGIN_FAILURE)
  // e investigação de Compliance/Auditor (CONFIG_CHANGE, AUTH_FAILURE).
  // raw_log também aberto — pra NOC pode mostrar comando executado,
  // pra Compliance/Auditor pode ser evidência forense.
  "infra.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "infra.editar": ["master"],
  "infra.raw_log": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "infra.dashboard": ["master", "seguranca_admin", "noc", "compliance", "auditor"],

  // Módulo de Circuitos — inventário de serviços/circuitos e automação nos
  // switches de core. VER é aberto aos admins (NOC consulta durante
  // troubleshooting). EDITAR mexe no inventário. EXECUTAR dispara job que
  // escreve em equipamento de produção — restrito a NOC + governança.
  // APROVAR é segregado de propósito: quem aprova não pode ser quem solicitou
  // (regra de dupla aprovação em provisionamento e prefixo).
  "circuitos.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor", "financeiro"],
  "circuitos.editar": ["master", "seguranca_admin", "noc"],
  "circuitos.executar": ["master", "seguranca_admin", "noc"],
  /**
   * Pedir/aprovar/cancelar BLOQUEIO e DESBLOQUEIO de cliente — e nada mais.
   *
   * Separada de `circuitos.executar` de propósito: aquela também libera a
   * reconciliação, que é trabalho de rede e não do financeiro. Reaproveitar a
   * permissão "que já existe" seria dar contrabando junto — o financeiro
   * ganharia poder que ninguém decidiu conceder.
   *
   * O financeiro APROVA porque a regra "aprovador ≠ solicitante" precisa de
   * duas pessoas do mesmo time: uma pede, a outra aprova, sem depender do NOC
   * para liberar uma fatura paga às 18h.
   */
  "circuitos.bloquear": ["master", "seguranca_admin", "noc", "financeiro"],
  "circuitos.aprovar": ["master", "seguranca_admin"],
  /**
   * EXCLUIR circuito do inventário. Separada de `circuitos.editar` porque
   * apagar não é editar: editar erra e se conserta olhando a tela, apagar erra
   * e some do lugar onde se olharia.
   *
   * Existe porque o cadastro é manual e errar faz parte — o NOC pegou dois ou
   * três cadastros errados na primeira semana (28/08/2026). Sem exclusão, o
   * inventário acumula lixo que vira divergência para sempre.
   *
   * NÃO toca o equipamento: apagar o cadastro não desconfigura a Vlanif. Se o
   * circuito estiver de fato no switch, ele reaparece como "não cadastrado" na
   * próxima reconciliação — que é o comportamento correto.
   */
  "circuitos.excluir": ["master", "seguranca_admin", "noc"],

  // DNS Manager — gestão de registros na API autoritativa USE
  // (dns-api.usetelecom.com.br). VER é aberto pros admins (NOC consulta PTR
  // durante troubleshooting). GERENCIAR (upsert/editar/remover PTR) é mutação
  // em infra crítica — restrito a NOC + governança, sempre com confirmação
  // ANTES→DEPOIS e auditoria de toda operação.
  "dns.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "dns.gerenciar": ["master", "seguranca_admin", "noc"],

  // Geofeed RFC 8805 — gestão de prefixos geolocalizados publicados para
  // registro.br (CSV público em /geofeed.csv). VER aberto pros admins (NOC
  // confere se o prefixo do cliente está geo-correto durante troubleshooting
  // de "IP aparece como SP no whois"). GERENCIAR (editar coordenada, habilitar/
  // desabilitar publicação, sync phpIPAM) restrito a NOC + governança porque
  // afeta como o tráfego é roteado/registrado externamente.
  "geofeed.ver": ["master", "seguranca_admin", "noc", "compliance", "auditor"],
  "geofeed.gerenciar": ["master", "seguranca_admin", "noc"],

  // Usuários
  "usuarios.ver": ["master", "seguranca_admin", "auditor"],
  "usuarios.editar": ["master", "seguranca_admin"],
} as const satisfies Record<string, readonly Role[]>;

export type Permissao = keyof typeof PERMISSOES;

export function podeExecutar(role: Role, permissao: Permissao): boolean {
  const permitidos = PERMISSOES[permissao] as readonly Role[];
  return permitidos.includes(role);
}

/**
 * Hierarquia de roles para prevenir escalada de privilégio.
 * Quanto maior o número, mais poder. Regra: usuário X só pode criar/editar
 * outro usuário Y se NIVEL_ROLE[X.role] >= NIVEL_ROLE[Y.role].
 *
 * Ex.: seguranca_admin (3) NÃO pode criar master (4) — bloqueia o ataque
 * "seguranca_admin se promove via criação de master pra logar como ele".
 */
export const NIVEL_ROLE: Record<Role, number> = {
  master: 4,
  seguranca_admin: 3,
  noc: 2,
  compliance: 2,
  auditor: 1,
  /**
   * Nível 1: não gerencia ninguém. O financeiro não cria nem edita usuário —
   * a hierarquia impede que o papel vire porta de entrada para escalar.
   */
  financeiro: 1,
};

export function podeGerenciarRole(criadorRole: Role, alvoRole: Role): boolean {
  return NIVEL_ROLE[criadorRole] >= NIVEL_ROLE[alvoRole];
}

/**
 * Domínios permitidos para cadastro de usuário. Mesmo que CF Access deixe
 * passar, a API recusa cadastrar usuário fora desta lista — defesa em
 * profundidade. Alterar exige deploy.
 */
export const DOMINIOS_PERMITIDOS = ["usetelecom.com.br"] as const;

export function dominioPermitido(email: string): boolean {
  const m = email.toLowerCase().match(/^[^@]+@(.+)$/);
  if (!m) return false;
  return (DOMINIOS_PERMITIDOS as readonly string[]).includes(m[1]!);
}
